import { useCallback, useRef, useState, type ReactNode } from "react";
import { ConfirmContext, type ConfirmFn, type ConfirmOptions } from "../../hooks/useConfirm.js";
import { ConfirmDialog } from "./ConfirmDialog.js";
import { useT } from "@mmm/ui";
interface ConfirmRequest {
  title: string;
  message: string;
  confirmLabel: string;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);
  const t = useT();

  // The default label is evaluated when confirm() is called, so it follows
  // the UI language at that moment (like the caller-supplied strings do).
  const confirm = useCallback<ConfirmFn>(({ title, message, confirmLabel = t("common.delete") }: ConfirmOptions) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setRequest({ title, message, confirmLabel });
    });
  }, [t]);

  const settle = (value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setRequest(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {request && (
        <ConfirmDialog
          title={request.title}
          message={request.message}
          confirmLabel={request.confirmLabel}
          onConfirm={() => settle(true)}
          onCancel={() => settle(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

// Compatibility export for existing screens while hooks migrate to hooks/useConfirm.
// eslint-disable-next-line react-refresh/only-export-components
export { useConfirm } from "../../hooks/useConfirm.js";
